import { FC, useState, ChangeEvent, useEffect } from "react"
import styled from "styled-components"
import { ContentfulProductWithShopifyData } from "@lib/types"
import { ProductVariant } from "shopify-storefront-api-typings"
import { decode } from "shopify-gid"
import { CompleteTheLookThumbnail } from "./index"
import {
  useDictionary,
  useCheckout,
  useUI,
  useCustomer,
  useTracking,
} from "@hooks/index"
import { SectionWithTitle, Button } from "@components/ui"

interface Props {
  products: ContentfulProductWithShopifyData[]
}

export const CompleteTheLook: FC<Props> = ({ products }) => {
  const { openCart, clickAddToBag } = useUI()
  const { id, addLineItems } = useCheckout()
  const [selectedVariants, setSelectedVariants] = useState<ProductVariant[]>([])
  const { currency, getCurrencyPrice, formatCurrencyPrice } = useCustomer()
  const { track } = useTracking()
  const [loading, setLoading] = useState(false)
  
  useEffect(() => {
    const allVariants = products.map(product => product.shopifyProduct.variants.edges[0].node)
    setSelectedVariants(allVariants)
  }, [products])

  const handleChecked = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target
    const { checked, value } = target

    const obj = JSON.parse(value)

    if (checked) {
      setSelectedVariants([...selectedVariants.filter(s => s.id !== obj.id), obj])
    } else {
      setSelectedVariants(selectedVariants.filter(s => s.id !== obj.id))
    }
  }

  const totalPrice = selectedVariants.reduce((a, v) => a + getCurrencyPrice(v), 0)

  const addToCart = async () => {
    const payload = selectedVariants.map(s => ({
      quantity: 1,
      variantId: s.id,
    }))
    setLoading(true)
    await addLineItems(payload)
    setLoading(false)
    openCart()
    clickAddToBag()

    const trackProductsAdded = selectedVariants.map(s => ({
      cart_id: decode(id).id,
      product_id: decode(s?.id).id,
      variant: s?.title,
      sku: s?.sku,
      price: parseFloat(s?.priceV2?.amount),
      brand: "Haute Hijab",
      quantity: 1,
      image_url: s?.image?.transformedSrc,
    }))

    track("Complete the Look Added", {
      currency,
      products: trackProductsAdded,
    })
    setSelectedVariants([])
  }

  return (
    <SectionWithTitle
      title="Complete the look"
      preTitle="Recommended"
      backgroundColor={{ name: "beige", value: "#F3EDEA" }}
    >
      <Container>
        <Products>
          {products.map(product => {
            return (
              <CompleteTheLookThumbnail
                key={product.sys.id}
                product={product}
                selected={selectedVariants}
                handleChecked={handleChecked}
              />
            )
          })}
          <AddToCartPanel>
            <AddToCartInner>
              <TitleWrapper>
                <Title>Total price</Title>
                <Price>{`${formatCurrencyPrice(totalPrice)}`}</Price>
              </TitleWrapper>
              <Button
                buttonTheme="ghostBlackReverse"
                title={"Add to bag"}
                disabled={selectedVariants.length === 0}
                onClick={addToCart}
                loading={loading}
              />
            </AddToCartInner>
          </AddToCartPanel>
        </Products>
      </Container>
    </SectionWithTitle>
  )
}

const Container = styled.div`
  margin: 0 auto;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoint.xl}px) {
    max-width: 110rem;
  }
`

const Products = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  justify-items: center;
  grid-gap: 2.7rem 1.5rem;
  align-items: flex-start;

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    grid-gap: 2.1rem;
  }
`

const AddToCartPanel = styled.div`
  position: relative;
  background: ${({ theme }) => theme.color.white};
  width: 100%;
  grid-column: -3/-1;

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    padding-top: 155%;
    grid-column: initial;
  }
`

const AddToCartInner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.8rem 2rem;

  @media (min-width: ${({ theme }) => theme.breakpoint.lg}px) {
    position: absolute;
    top: 0;
    flex-direction: column;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  font-weight: 300;
`

const Title = styled.div`
  margin: 0 0 0.3rem;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1.67px;
  line-height: 2.4rem;
`

const Price = styled.div`
  margin: 0 0 1.4rem;
  font-family: ${({ theme }) => theme.font.heading};
  font-size: 3rem;
  line-height: 4.3rem;
  letter-spacing: 0.12em;
`
